import React, { useState, useEffect } from 'react';
import { useAppSelector } from '../hooks/useAppSelector';
import { useAppDispatch } from '../hooks/useAppDispatch';
import { markOrderAsRead, selectAllOrders } from '../store/slices/orderSlice';
import axios from 'axios';
import { useOrderNotifications } from '../hooks/useOrderNotifications';
import { API_ENDPOINTS } from '../config/api';
import { playNotificationSound } from '../utils/notificationSound';
import OrderDetailModal from './OrderDetailModal';

const OrderNotifications = () => {
    const dispatch = useAppDispatch();
    const { user, restaurant } = useAppSelector(state => state.auth);
    const { orders } = useAppSelector(selectAllOrders);
    const restaurantId = restaurant?._id || user?.id;
    const [showNotifications, setShowNotifications] = useState(false);
    const [lastOrderCount, setLastOrderCount] = useState(0);
    const [selectedOrder, setSelectedOrder] = useState(null);

    // Use real-time notifications hook
    useOrderNotifications(restaurantId);
    
    // Filter recent orders (last 24 hours)
    const notifications = orders.filter(order => {
        const orderTime = new Date(order.orderDate);
        const now = new Date();
        return (now - orderTime) < 24 * 60 * 60 * 1000;
    });

    // Only count unseen notifications from recent (24h) orders
    const unreadRecentCount = notifications.filter(order => !order.isRead).length;

    // Check for new orders and play sound
    useEffect(() => {
        if (notifications.length > lastOrderCount && lastOrderCount > 0) {
            playNotificationSound();
        }
        setLastOrderCount(notifications.length);
    }, [notifications.length, lastOrderCount]);

    const markAsRead = async (orderId) => {
        try {
            await axios.put(API_ENDPOINTS.ORDER_BY_ID(orderId), { isRead: true });
            dispatch(markOrderAsRead(orderId));
        } catch (error) {
            console.error('Failed to mark notification as read:', error);
        }
    };

    /** Owner viewed the list — mark all recent (24h) unread orders as seen so badge goes to 0 */
    const markAllRecentNotificationsAsSeen = async () => {
        const now = Date.now();
        const oneDayMs = 24 * 60 * 60 * 1000;
        const unreadRecent = orders.filter((order) => {
            const orderTime = new Date(order.orderDate).getTime();
            if (Number.isNaN(orderTime)) return false;
            if (now - orderTime >= oneDayMs) return false;
            return !order.isRead;
        });
        if (unreadRecent.length === 0) return;
        await Promise.all(
            unreadRecent.map((o) =>
                axios.put(API_ENDPOINTS.ORDER_BY_ID(o._id), { isRead: true }).catch(() => {})
            )
        );
        unreadRecent.forEach((o) => dispatch(markOrderAsRead(o._id)));
    };

    const closeNotificationModal = async () => {
        await markAllRecentNotificationsAsSeen();
        setShowNotifications(false);
    };

    const toggleNotificationModal = () => {
        if (showNotifications) {
            void closeNotificationModal();
        } else {
            setShowNotifications(true);
        }
    };

    const openOrderModal = (order) => {
        setSelectedOrder(order);
        if (!order.isRead) {
            markAsRead(order._id);
        }
    };

    const formatTime = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffInMinutes = Math.floor((now - date) / (1000 * 60));
        
        if (diffInMinutes < 1) return 'Just now';
        if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
        if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
        return date.toLocaleDateString();
    };

    return (
        <div className="relative">
            {/* Notification Bell */}
            <button
                type="button"
                onClick={toggleNotificationModal}
                className="relative inline-flex items-center justify-center p-1.5 sm:p-2 rounded-lg bg-orange-500 text-white shadow-sm hover:bg-orange-600 transition-colors"
                aria-label="Recent orders"
            >
                <span className="text-sm">📋</span>
                {unreadRecentCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[1rem] h-4 px-0.5 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                        {unreadRecentCount > 99 ? '99+' : unreadRecentCount}
                    </span>
                )}
            </button>

            {/* Recent Orders — centered modal (same as Waiter Request) */}
            {showNotifications && (
                <div
                    role="presentation"
                    className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/60 backdrop-blur-sm p-3 sm:p-4"
                    onClick={() => void closeNotificationModal()}
                >
                    <div
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="recent-orders-title"
                        className="bg-white rounded-xl shadow-2xl border border-stone-200 w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-3 sm:px-5 sm:py-3 border-b border-stone-200 bg-stone-50/95 backdrop-blur">
                            <div>
                                <p id="recent-orders-title" className="text-[10px] font-semibold uppercase tracking-[0.14em] text-stone-500">Recent orders</p>
                                <p className="mt-0.5 text-xs text-stone-600">
                                    {notifications.length === 0 ? 'No orders in the last 24 hours' : `${notifications.length} order${notifications.length > 1 ? 's' : ''} · last 24h`}
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => void closeNotificationModal()}
                                className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-stone-200 bg-white text-stone-500 hover:bg-stone-100 hover:text-stone-800 transition-colors"
                                aria-label="Close"
                            >
                                ×
                            </button>
                        </div>

                        {/* Content */}
                        <div className="flex-1 overflow-y-auto px-4 pb-4 pt-3 sm:px-5 sm:pt-4 sm:pb-5">
                            {notifications.length === 0 ? (
                                <div className="flex flex-col items-center justify-center py-10 text-center">
                                    <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-stone-100 text-2xl">📭</div>
                                    <p className="text-sm font-semibold text-stone-800">No recent orders</p>
                                    <p className="mt-1 text-xs text-stone-500 max-w-xs">Orders from the last 24 hours appear here.</p>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {notifications.map((order) => (
                                        <button
                                            key={order._id}
                                            type="button"
                                            className={`w-full text-left rounded-xl border p-3 transition-colors ${
                                                !order.isRead
                                                    ? 'bg-orange-50/80 border-orange-200 border-l-4 border-l-orange-500'
                                                    : 'bg-white border-stone-200 hover:bg-stone-50'
                                            }`}
                                            onClick={() => openOrderModal(order)}
                                        >
                                            <div className="flex items-start gap-3">
                                                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-orange-100 text-orange-600 text-sm">🍽️</span>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center justify-between gap-2 mb-0.5">
                                                        <span className="text-xs font-semibold text-stone-800 truncate">
                                                            Table {order.tableId?.tableNumber || 'Counter'}
                                                        </span>
                                                        <span className="text-[11px] text-stone-500 shrink-0">{formatTime(order.orderDate)}</span>
                                                    </div>
                                                    <p className="text-[11px] text-stone-600 truncate">👤 {order.customerName} · 📱 {order.mobileNumber}</p>
                                                    <p className="text-[11px] text-stone-500 mt-0.5 line-clamp-2">
                                                        {order.items.slice(0, 2).map((item, idx) => (
                                                            <span key={idx}>{item.name} ×{item.quantity}{idx < Math.min(order.items.length, 2) - 1 ? ', ' : ''}</span>
                                                        ))}
                                                        {order.items.length > 2 && ` +${order.items.length - 2} more`}
                                                    </p>
                                                    <div className="flex items-center justify-between mt-1.5">
                                                        <span className="text-xs font-bold text-emerald-600">₹{order.finalAmount?.toFixed(2)}</span>
                                                        {!order.isRead && <span className="w-2 h-2 rounded-full bg-orange-500 shrink-0" />}
                                                    </div>
                                                </div>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="sticky bottom-0 z-10 border-t border-stone-200 bg-white/95 backdrop-blur-sm px-4 py-3 sm:px-5 sm:py-3">
                            <button
                                type="button"
                                onClick={() => void closeNotificationModal()}
                                className="w-full inline-flex items-center justify-center rounded-full bg-stone-900 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-stone-800 hover:shadow-md transition-colors"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {selectedOrder && (
                <OrderDetailModal order={selectedOrder} onClose={() => setSelectedOrder(null)} />
            )}
            
        </div>
    );
};

export default OrderNotifications;